// Per-trial validity guards.
//
// No row is ever dropped in the app -- rows are flagged, and the raw
// indicators are kept so the exclusion rule can be re-derived (or argued with)
// during the week 10 analysis. See docs/timing.md.

import { rawContext } from '../clock.js'

export function createGuardMonitor() {
  let trial = null
  let rafId = null
  let lastFrame = null

  const onHidden = () => { if (trial) trial.page_hidden = true }
  const onBlur = () => { if (trial) trial.window_blur = true }
  const onVisibility = () => { if (document.hidden) onHidden() }

  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pagehide', onHidden)
  window.addEventListener('blur', onBlur)

  // A stalled main thread delays event dispatch and rAF alike, so the longest
  // frame during a trial is the best single indicator that its timings are junk.
  function watchFrames(t) {
    if (trial) {
      if (lastFrame !== null) {
        const delta = t - lastFrame
        if (delta > trial.long_frame_ms) trial.long_frame_ms = delta
      }
      lastFrame = t
      rafId = requestAnimationFrame(watchFrames)
    }
  }

  return {
    beginTrial() {
      const c = rawContext()
      trial = {
        page_hidden: document.hidden,
        window_blur: !document.hasFocus(),
        long_frame_ms: 0,
        ctx_state_cue: c.state,
        ctx_state_resp: '',
        ctx_resumed_in_trial: false,
        sample_rate_cue: c.sampleRate,
      }
      lastFrame = null
      rafId = requestAnimationFrame(watchFrames)
      return trial
    },

    // Call when the context is resumed mid-trial (visibility return, iOS
    // interruption) so the row can be marked rather than quietly trusted.
    noteResume() { if (trial) trial.ctx_resumed_in_trial = true },

    endTrial() {
      if (!trial) return {}
      const c = rawContext()
      trial.ctx_state_resp = c.state
      trial.sample_rate_resp = c.sampleRate
      if (rafId) cancelAnimationFrame(rafId)
      rafId = null
      const out = trial
      trial = null
      return out
    },

    dispose() {
      if (rafId) cancelAnimationFrame(rafId)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onHidden)
      window.removeEventListener('blur', onBlur)
    },
  }
}

// The published exclusion rule. Kept in one place so the app, the codebook and
// the week 10 notebook cannot drift apart.
export function assessValidity(row) {
  const reasons = []
  if (row.reaction_time_ms !== '' && row.reaction_time_ms != null && row.reaction_time_ms < 100) reasons.push('anticipated')
  if (row.timed_out) reasons.push('timeout')
  if (row.movement_time_ms < 0 || row.response_time_ms < 0) reasons.push('clock_err')
  if (row.page_hidden) reasons.push('hidden')
  if (row.window_blur) reasons.push('blur')
  if (row.ctx_state_cue !== 'running' || row.ctx_state_resp !== 'running') reasons.push('ctx')
  if (row.pointer_cancelled) reasons.push('cancel')
  if (row.down_before_cue) reasons.push('early_down')
  if (row.hand_in_target_at_cue) reasons.push('in_target')
  if (row.long_frame_ms > 100) reasons.push('stall')
  if (row.cue_painted === false) reasons.push('cue_estimated')
  if (row.cam_period_ms > 50) reasons.push('low_fps')
  return { valid: reasons.length === 0, invalid_reason: reasons.join('|') }
}
